import { localized, msg } from '@lit/localize';
import { css, html, LitElement, type PropertyValues } from 'lit';
import { live } from 'lit/directives/live.js';
import { repeat } from 'lit/directives/repeat.js';
import { api } from '../../../../api';
import type { EmailAccount } from '../../../../api/email-accounts';
import type {
  KlardatenCredentialsDto,
  KlardatenInstance,
  KlardatenProfile,
} from '../../../../api/klardaten-credentials';
import type {
  Id,
  RuntimeValue,
  TaskApprovalGuards,
  TaskDefinition,
} from '../../../../api/types';
import '@spectrum-web-components/textfield/sp-textfield.js';
import '@spectrum-web-components/tooltip/sp-tooltip.js';
import '@spectrum-web-components/switch/sp-switch.js';
import '@spectrum-web-components/picker/sp-picker.js';
import '@spectrum-web-components/action-button/sp-action-button.js';
import '@spectrum-web-components/action-menu/sp-action-menu.js';
import '@spectrum-web-components/menu/sp-menu.js';
import '@spectrum-web-components/menu/sp-menu-item.js';
import '@spectrum-web-components/overlay/overlay-trigger.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-full-screen.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-full-screen-exit.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-visibility.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-visibility-off.js';
import './code-editor';
import { debounce } from 'lodash';
import {
  getKlardatenInstanceLabel,
  getKlardatenProfilePrimaryLabel,
  getKlardatenProfileSecondaryLabel,
} from '../../../../utils/klardaten-display';
import { propertyGridStyles } from '../property-grid.styles';
import {
  enhanceConfigDescriptionForEmailTask,
  filterConfigFields,
  getConfigEntries,
  getOptionDescription,
  getOptionLabel,
  getOptionValue,
  getPickerPlaceholderLabel,
  type InspectorFieldDescription,
  type InspectorFieldEntries,
  type InspectorFieldOption,
  isEmailRelatedTask,
  isSecretField,
  isStackedField,
  normalizeConfigDisplayValue,
  parseConfigValue,
  resolveConfigRawValue,
  resolveEditorRows,
  resolveTextareaRows,
  shouldUseMultiline,
} from './inspector-utils';

const CONNECTION_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_$]*$/;
type InspectorCommitContext = {
  workflowId?: Id;
  taskId?: Id;
  definition?: TaskDefinition;
};
type KlardatenTaskConfigShape = {
  authKeys: string[];
  authMode: 'native' | 'n8n';
  instanceKey: string;
  profileKey: string;
};

function asNonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export abstract class BaseInspector extends LitElement {
  static styles = [
    propertyGridStyles,
    css`
    :host {
      width: 100%;
    }
    section {
      margin-bottom: 0;
    }
    .secret-field-control {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 4px;
      min-width: 0;
    }
    .secret-field-input {
      flex: 1 1 auto;
      min-width: 0;
      width: 100%;
      font-size: 12px;
      --mod-textfield-background-color: var(--inspector-control-bg);
      --spectrum-textfield-background-color: var(--inspector-control-bg);
      --mod-textfield-placeholder-font-size: 12px;
      --mod-textfield-border-width: 0px;
      --mod-textfield-focus-indicator-width: 0px;
    }
    .secret-toggle-button {
      flex: 0 0 auto;
      width: 28px;
      min-width: 28px;
      height: 28px;
      --mod-actionbutton-icon-size: 13px;
      --mod-actionbutton-padding-inline: 0px;
      --mod-actionbutton-background-color-default: var(--inspector-control-bg);
      --mod-actionbutton-background-color-hover: var(--inspector-control-bg);
      --mod-actionbutton-background-color-down: var(--inspector-control-bg);
    }
    .field-control-with-reference {
      width: 100%;
      display: flex;
      align-items: flex-start;
      gap: 4px;
      min-width: 0;
    }
    .field-control-with-reference > :first-child {
      flex: 1 1 auto;
      min-width: 0;
    }
    .runtime-reference-menu {
      flex: 0 0 auto;
      min-width: 32px;
      height: 28px;
      --mod-actionbutton-padding-inline: 6px;
    }
    .klardaten-setup-hint {
      align-items: center;
      background: color-mix(
        in srgb,
        var(--color-sidebar-selected-border, #3f63f4) 8%,
        var(--inspector-control-bg, #f8fafc)
      );
      border: 1px solid
        color-mix(
          in srgb,
          var(--color-sidebar-selected-border, #3f63f4) 22%,
          var(--inspector-pane-separator-color, #dde3ea)
        );
      border-radius: 6px;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
      display: grid;
      gap: 10px;
      grid-template-columns: minmax(0, 1fr) auto;
      margin: 4px 0 10px;
      overflow: hidden;
      padding: 8px 10px 8px 12px;
      position: relative;
    }
    .klardaten-setup-hint::before {
      background: var(--color-sidebar-selected-border, #3f63f4);
      bottom: 0;
      content: '';
      left: 0;
      position: absolute;
      top: 0;
      width: 3px;
    }
    .klardaten-setup-hint-copy {
      display: flex;
      flex-direction: column;
      gap: 1px;
      min-width: 0;
    }
    .klardaten-setup-hint-title {
      color: var(--color-app-text, #1f2937);
      font-size: 11px;
      font-weight: 650;
      line-height: 1.3;
    }
    .klardaten-setup-hint-text {
      color: var(--color-app-text-secondary, #6b7280);
      font-size: 11px;
      line-height: 1.35;
      margin: 0;
    }
    .klardaten-setup-hint-button {
      align-items: center;
      background: var(--inspector-control-bg, #ffffff);
      border: 1px solid
        color-mix(
          in srgb,
          var(--color-sidebar-selected-border, #3f63f4) 24%,
          transparent
        );
      border-radius: 5px;
      color: var(--color-sidebar-selected-border, #3f63f4);
      cursor: pointer;
      display: inline-flex;
      flex: 0 0 auto;
      font: inherit;
      font-size: 11px;
      font-weight: 650;
      line-height: 1.2;
      min-height: 24px;
      padding: 3px 8px;
      white-space: nowrap;
    }
    .klardaten-setup-hint-button:hover {
      background: color-mix(
        in srgb,
        var(--color-sidebar-selected-border, #3f63f4) 8%,
        var(--inspector-control-bg, #ffffff)
      );
    }
    .klardaten-setup-hint-button:focus-visible {
      outline: 2px solid var(--color-sidebar-selected-border, #3f63f4);
      outline-offset: 2px;
    }
    @media (max-width: 520px) {
      .klardaten-setup-hint {
        align-items: flex-start;
        flex-direction: column;
      }
    }
    `,
  ];
}

@localized()
export class SbTaskInspector extends BaseInspector {
  static properties = {
    workflowId: {},
    taskId: {},
    config: { state: true },
    approvalGuards: { state: true },
    approvalGuardError: { state: true },
    definition: { state: true },
    label: { state: true },
    emailAccounts: { state: true },
    klardatenCredentials: { state: true },
    klardatenInstances: { state: true },
    klardatenProfiles: { state: true },
    runtimeValues: { state: true },
    fullscreenConfigKey: { state: true },
  } as const;

  declare workflowId?: Id;
  declare taskId?: Id;
  declare config?: Record<string, unknown>;
  declare approvalGuards?: TaskApprovalGuards;
  declare approvalGuardError?: string;
  declare definition?: TaskDefinition;
  declare label?: string | null;
  declare emailAccounts?: EmailAccount[];
  declare klardatenCredentials?: KlardatenCredentialsDto | null;
  declare klardatenInstances: KlardatenInstance[];
  declare klardatenProfiles: KlardatenProfile[];
  declare runtimeValues: RuntimeValue[];
  declare fullscreenConfigKey?: string;
  private klardatenProfilesInstanceId?: string | null;
  private klardatenProfilesRequestId = 0;
  private pendingConfigKeys = new Set<string>();
  private pendingConfigValues = new Map<string, unknown>();
  private revealedSecretKeys = new Set<string>();
  private pendingLabelDirty = false;
  private pendingLabelValue?: string;

  constructor() {
    super();
    this.workflowId = undefined;
    this.taskId = undefined;
    this.config = undefined;
    this.approvalGuards = undefined;
    this.approvalGuardError = undefined;
    this.definition = undefined;
    this.label = undefined;
    this.emailAccounts = undefined;
    this.klardatenCredentials = undefined;
    this.klardatenInstances = [];
    this.klardatenProfiles = [];
    this.runtimeValues = [];
  }

  async connectedCallback() {
    super.connectedCallback();
    await Promise.all([
      this.loadEmailAccounts(),
      this.loadRuntimeValues(),
      this.loadKlardatenDefaults(),
    ]);
    window.addEventListener(
      'runtimeValueCreated',
      this.onRuntimeValueUpserted as EventListener
    );
    window.addEventListener(
      'runtimeValueChanged',
      this.onRuntimeValueUpserted as EventListener
    );
    window.addEventListener(
      'runtimeValueRemoved',
      this.onRuntimeValueRemoved as EventListener
    );
  }

  disconnectedCallback(): void {
    window.removeEventListener(
      'runtimeValueCreated',
      this.onRuntimeValueUpserted as EventListener
    );
    window.removeEventListener(
      'runtimeValueChanged',
      this.onRuntimeValueUpserted as EventListener
    );
    window.removeEventListener(
      'runtimeValueRemoved',
      this.onRuntimeValueRemoved as EventListener
    );
    this.flushPendingChanges();
    super.disconnectedCallback();
  }

  protected override willUpdate(changed: PropertyValues<this>): void {
    const taskChanged = changed.has('taskId');
    const workflowChanged = changed.has('workflowId');
    if (!taskChanged && !workflowChanged) return;

    const previousContext: InspectorCommitContext = {
      workflowId: workflowChanged
        ? (changed.get('workflowId') as Id | undefined)
        : this.workflowId,
      taskId: taskChanged
        ? (changed.get('taskId') as Id | undefined)
        : this.taskId,
      definition: (changed.has('definition')
        ? changed.get('definition')
        : this.definition) as TaskDefinition | undefined,
    };
    this.flushPendingChanges(previousContext);
    if (taskChanged || workflowChanged) {
      this.revealedSecretKeys.clear();
    }
  }

  protected override updated(changed: PropertyValues<this>): void {
    if (
      changed.has('definition') ||
      changed.has('config') ||
      changed.has('klardatenCredentials')
    ) {
      void this.ensureKlardatenProfilesForCurrentTask();
    }
  }

  private async loadEmailAccounts() {
    try {
      const accounts = await api.emailAccounts.list();
      this.emailAccounts = accounts;

      // Auto-select first email account if none is selected for email-related tasks
      if (
        isEmailRelatedTask(this.definition) &&
        accounts.length > 0 &&
        !this.config?.emailAddress
      ) {
        this.config = {
          ...(this.config ?? {}),
          emailAddress: accounts[0].email,
        };
        if (this.workflowId && this.taskId != null) {
          const types = this.definition?.configDescription?.emailAddress?.types;
          await this.persistConfigChange(
            this.workflowId,
            this.taskId,
            'emailAddress',
            accounts[0].email,
            types
          );
        }
      }

      this.requestUpdate();
    } catch (error) {
      console.error('Failed to load email accounts:', error);
      this.emailAccounts = [];
    }
  }

  private async loadRuntimeValues() {
    try {
      this.runtimeValues = await api.runtimeValues.list();
    } catch (error) {
      console.error('Failed to load runtime values:', error);
      this.runtimeValues = [];
    }
  }

  private async loadKlardatenDefaults() {
    this.clearKlardatenProfileCache();
    try {
      const credentials = await api.klardatenCredentials.get();
      this.klardatenCredentials = credentials;
      if (!credentials.hasKey) {
        this.klardatenInstances = [];
        this.clearKlardatenProfileCache();
        return;
      }

      this.klardatenInstances = await api.klardatenCredentials.listInstances();
      await this.ensureKlardatenProfilesForCurrentTask();
    } catch {
      this.klardatenCredentials = null;
      this.klardatenInstances = [];
      this.clearKlardatenProfileCache();
    }
  }

  private isKlardatenTaskDefinition(
    definition: TaskDefinition | undefined = this.definition
  ): boolean {
    return Boolean(this.getKlardatenTaskConfigShape(definition));
  }

  private getKlardatenTaskConfigShape(
    definition: TaskDefinition | undefined = this.definition
  ): KlardatenTaskConfigShape | null {
    const desc = definition?.configDescription;
    const isNativeKlardatenTask =
      definition?.category === 'datevconnect' ||
      definition?.category === 'klardaten';
    if (
      isNativeKlardatenTask &&
      desc?.clientInstance &&
      desc?.apiKey &&
      desc?.username &&
      desc?.password
    ) {
      return {
        authKeys: ['host', 'apiKey', 'username', 'password'],
        authMode: 'native',
        instanceKey: 'clientInstance',
        profileKey: 'profileId',
      };
    }

    const isDatevConnectN8nTask =
      definition?.category === 'datevconnect' ||
      definition?.name?.startsWith('datevconnect-');
    if (
      isDatevConnectN8nTask &&
      desc?.host &&
      desc?.apiKey &&
      desc?.email &&
      desc?.password &&
      desc?.clientInstanceId
    ) {
      return {
        authKeys: ['host', 'email', 'password', 'apiKey'],
        authMode: 'n8n',
        instanceKey: 'clientInstanceId',
        profileKey: 'profileId',
      };
    }

    return null;
  }

  private hasExplicitKlardatenNodeAuth(
    shape: KlardatenTaskConfigShape | null = this.getKlardatenTaskConfigShape()
  ): boolean {
    if (!shape) return false;
    const config = this.config ?? {};
    const hasValue = (key: string) => Boolean(asNonEmptyString(config[key]));

    if (shape.authMode === 'native') {
      return (
        hasValue('apiKey') || (hasValue('username') && hasValue('password'))
      );
    }

    return hasValue('apiKey') || (hasValue('email') && hasValue('password'));
  }

  private shouldShowKlardatenSetupHint(): boolean {
    return (
      this.klardatenCredentials !== undefined &&
      !this.klardatenCredentials?.hasKey &&
      this.isKlardatenTaskDefinition()
    );
  }

  private openKlardatenSettingsFromHint(e: Event): void {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent('switch-module', {
        detail: { id: 'settings' },
        bubbles: true,
        composed: true,
      })
    );
  }

  private getEffectiveKlardatenInstanceId(
    clientInstanceValue?: unknown
  ): string | null {
    const shape = this.getKlardatenTaskConfigShape();
    const rawValue =
      clientInstanceValue !== undefined
        ? clientInstanceValue
        : shape
          ? this.config?.[shape.instanceKey]
          : undefined;
    const configured = asNonEmptyString(rawValue);
    if (configured) return configured;
    return this.klardatenCredentials?.defaultClientInstanceId ?? null;
  }

  private async ensureKlardatenProfilesForCurrentTask() {
    const shape = this.getKlardatenTaskConfigShape();
    if (
      !this.klardatenCredentials?.hasKey ||
      !shape ||
      this.hasExplicitKlardatenNodeAuth(shape)
    ) {
      return;
    }

    await this.loadKlardatenProfilesForInstance(
      this.getEffectiveKlardatenInstanceId()
    );
  }

  private async loadKlardatenProfilesForInstance(
    instanceId: string | null,
    options: { force?: boolean } = {}
  ): Promise<KlardatenProfile[] | null> {
    if (!instanceId) {
      this.clearKlardatenProfileCache();
      return [];
    }
    if (!options.force && this.klardatenProfilesInstanceId === instanceId) {
      return this.klardatenProfiles;
    }

    const requestId = ++this.klardatenProfilesRequestId;
    const context = {
      workflowId: this.workflowId,
      taskId: this.taskId,
      taskName: this.definition?.name,
      instanceId,
    };

    try {
      const profiles = await api.klardatenCredentials.listProfiles(instanceId);
      if (!this.isCurrentKlardatenProfileRequest(requestId, context)) {
        return null;
      }
      this.klardatenProfiles = profiles;
      this.klardatenProfilesInstanceId = instanceId;
      return this.klardatenProfiles;
    } catch {
      if (!this.isCurrentKlardatenProfileRequest(requestId, context)) {
        return null;
      }
      this.clearKlardatenProfileCache();
      return null;
    }
  }

  private clearKlardatenProfileCache(): void {
    this.klardatenProfilesRequestId += 1;
    this.klardatenProfiles = [];
    this.klardatenProfilesInstanceId = null;
  }

  private isCurrentKlardatenProfileRequest(
    requestId: number,
    context: {
      workflowId?: Id;
      taskId?: Id;
      taskName?: string;
      instanceId: string;
    }
  ): boolean {
    return (
      requestId === this.klardatenProfilesRequestId &&
      this.workflowId === context.workflowId &&
      this.taskId === context.taskId &&
      this.definition?.name === context.taskName &&
      this.getEffectiveKlardatenInstanceId() === context.instanceId
    );
  }

  private formatSettingsDefaultLabel(label?: string | null): string {
    return label ? `Settings default: ${label}` : 'Settings default';
  }

  private getKlardatenDefaultInstanceLabel(): string {
    const defaultInstanceId =
      this.klardatenCredentials?.defaultClientInstanceId;
    const defaultInstance = this.klardatenInstances.find(
      (instance) => instance.id === defaultInstanceId
    );
    return this.formatSettingsDefaultLabel(
      defaultInstance ? getKlardatenInstanceLabel(defaultInstance) : null
    );
  }

  private getKlardatenDefaultProfileOption(): InspectorFieldOption {
    const defaultProfileId = this.klardatenCredentials?.defaultProfileId;
    const defaultProfile = this.klardatenProfiles.find(
      (profile) => profile.profile_id === defaultProfileId
    );
    return {
      value: '',
      label: this.formatSettingsDefaultLabel(
        defaultProfile ? getKlardatenProfilePrimaryLabel(defaultProfile) : null
      ),
      description: defaultProfile
        ? getKlardatenProfileSecondaryLabel(defaultProfile)
        : undefined,
    };
  }

  private onRuntimeValueUpserted = (event: Event) => {
    const detail = (event as CustomEvent<{ id: string }>).detail;
    if (!detail?.id) return;
    void this.loadRuntimeValues();
  };

  private onRuntimeValueRemoved = (event: Event) => {
    const detail = (event as CustomEvent<{ id: string }>).detail;
    if (!detail?.id) return;
    void this.loadRuntimeValues();
  };

  private extractConfigEventValue(e: Event): unknown {
    const detail = (e as CustomEvent<{ value?: unknown }>).detail;
    if (detail && detail.value !== undefined) {
      return detail.value;
    }

    const target = e.target as
      | (EventTarget & {
          checked?: boolean;
          value?: unknown;
        })
      | null;
    return typeof target?.checked === 'boolean'
      ? target.checked
      : target?.value;
  }

  private onConfigInput(key: string, e: Event) {
    const value = this.extractConfigEventValue(e);
    this.config = {
      ...(this.config ?? {}),
      [key]: value,
    };
    this.pendingConfigKeys.add(key);
    this.pendingConfigValues.set(key, value);
  }

  private async onConfigCommitChange(key: string, e: Event) {
    const value = this.extractConfigEventValue(e);
    if (value === undefined) return;

    if (
      this.klardatenCredentials?.hasKey &&
      key === this.getKlardatenTaskConfigShape()?.instanceKey &&
      !this.hasExplicitKlardatenNodeAuth()
    ) {
      await this.persistKlardatenClientInstanceChange(value);
      return;
    }

    this.config = {
      ...(this.config ?? {}),
      [key]: value,
    };
    this.pendingConfigKeys.delete(key);
    this.pendingConfigValues.delete(key);
    const workflowId = this.workflowId;
    const taskId = this.taskId;
    const definition = this.definition;
    if (!workflowId || taskId == null) return;

    const types = definition?.configDescription?.[key]?.types;
    await this.persistConfigChange(workflowId, taskId, key, value, types);
  }

  private async persistKlardatenClientInstanceChange(value: unknown) {
    const shape = this.getKlardatenTaskConfigShape();
    if (!shape) return;

    const definition = this.definition;
    const types = definition?.configDescription?.[shape.instanceKey]?.types;
    const parsed = parseConfigValue(value, types);
    this.config = {
      ...(this.config ?? {}),
      [shape.instanceKey]: parsed,
    };
    this.pendingConfigKeys.delete(shape.instanceKey);
    this.pendingConfigValues.delete(shape.instanceKey);

    const currentProfileId = asNonEmptyString(this.config[shape.profileKey]);
    const profiles = await this.loadKlardatenProfilesForInstance(
      this.getEffectiveKlardatenInstanceId(parsed),
      { force: true }
    );
    const configPatch: Record<string, unknown> = {
      [shape.instanceKey]: parsed,
    };

    if (
      currentProfileId &&
      profiles &&
      !profiles.some((profile) => profile.profile_id === currentProfileId)
    ) {
      configPatch[shape.profileKey] = '';
      this.config = {
        ...(this.config ?? {}),
        [shape.profileKey]: '',
      };
      this.pendingConfigKeys.delete(shape.profileKey);
      this.pendingConfigValues.delete(shape.profileKey);
    }

    const workflowId = this.workflowId;
    const taskId = this.taskId;
    if (!workflowId || taskId == null) return;

    await this.persistConfigPatch(workflowId, taskId, configPatch);
  }

  private onConfigEditorCommit(key: string, e: Event) {
    if (!this.pendingConfigKeys.has(key)) return;

    const eventValue = this.extractConfigEventValue(e);
    const value =
      eventValue !== undefined
        ? eventValue
        : this.pendingConfigValues.has(key)
          ? this.pendingConfigValues.get(key)
          : this.config?.[key];
    if (value === undefined) return;

    this.pendingConfigKeys.delete(key);
    this.pendingConfigValues.delete(key);
    const workflowId = this.workflowId;
    const taskId = this.taskId;
    const definition = this.definition;
    if (!workflowId || taskId == null) return;

    const types = definition?.configDescription?.[key]?.types;
    void this.persistConfigChange(workflowId, taskId, key, value, types);
  }

  private onLabelInput(e: Event) {
    const target = e.target as HTMLInputElement;
    const value = target?.value ?? '';
    this.label = value;
    this.pendingLabelDirty = true;
    this.pendingLabelValue = value;
  }

  private onLabelChange(e: Event) {
    const target = e.target as HTMLInputElement;
    const value = target?.value ?? this.pendingLabelValue ?? this.label ?? '';
    this.pendingLabelDirty = false;
    this.pendingLabelValue = undefined;
    const workflowId = this.workflowId;
    const taskId = this.taskId;
    if (!workflowId || taskId == null) return;
    void this.persistLabelChange(workflowId, taskId, value);
  }

  private getControlByName(name: string) {
    const root = this.renderRoot;
    if (!root) {
      return undefined;
    }
    const controls = root.querySelectorAll<HTMLElement>('[name]');
    return Array.from(controls).find((el) => el.getAttribute('name') === name);
  }

  private focusControlByName(name: string) {
    const target = this.getControlByName(name) as
      | (HTMLElement & { focus?: () => void })
      | undefined;
    target?.focus?.();
  }

  private async persistLabelChange(workflowId: Id, taskId: Id, value: string) {
    try {
      await api.workflows.updateTask(workflowId, taskId, {
        label: value || null,
      });
      if (this.workflowId === workflowId && this.taskId === taskId) {
        this.label = value || null;
        this.dispatchEvent(
          new CustomEvent('task-label-changed', {
            detail: { value: this.label },
            bubbles: true,
            composed: true,
          })
        );
      }
    } catch (_err) {}
  }

  private async persistConfigChange(
    workflowId: Id,
    taskId: Id,
    key: string,
    value: unknown,
    types?: string[]
  ) {
    const parsed = parseConfigValue(value, types);
    await this.persistConfigPatch(workflowId, taskId, { [key]: parsed });
  }

  private async persistConfigPatch(
    workflowId: Id,
    taskId: Id,
    configPatch: Record<string, unknown>
  ) {
    try {
      await api.workflows.updateTask(workflowId, taskId, {
        config: configPatch,
      });
      if (this.workflowId === workflowId && this.taskId === taskId) {
        this.config = {
          ...(this.config ?? {}),
          ...configPatch,
        };
      }
      for (const [key, value] of Object.entries(configPatch)) {
        this.dispatchEvent(
          new CustomEvent('task-config-changed', {
            detail: { key, value, taskId },
            bubbles: true,
            composed: true,
          })
        );
      }
    } catch (_err) {
      // ignore errors here; parent component handles error state
    }
  }

  private flushPendingChanges(context?: InspectorCommitContext) {
    if (this.pendingConfigKeys.size === 0 && !this.pendingLabelDirty) return;

    const workflowId = context?.workflowId ?? this.workflowId;
    const taskId = context?.taskId ?? this.taskId;
    const definition = context?.definition ?? this.definition;
    if (!workflowId || taskId == null) return;

    if (this.pendingLabelDirty) {
      const value = this.pendingLabelValue ?? this.label ?? '';
      void this.persistLabelChange(workflowId, taskId, value);
    }

    for (const key of this.pendingConfigKeys) {
      const value = this.pendingConfigValues.has(key)
        ? this.pendingConfigValues.get(key)
        : this.config?.[key];
      if (value === undefined) continue;
      const types = definition?.configDescription?.[key]?.types;
      void this.persistConfigChange(workflowId, taskId, key, value, types);
    }

    this.pendingConfigKeys.clear();
    this.pendingConfigValues.clear();
    this.pendingLabelDirty = false;
    this.pendingLabelValue = undefined;
  }

  private getConfigEntries(): InspectorFieldEntries {
    const configDescription = this.definition?.configDescription as
      | Record<string, InspectorFieldDescription>
      | undefined;
    const enhancedDescription = enhanceConfigDescriptionForEmailTask(
      configDescription,
      this.definition,
      this.emailAccounts
    );
    const klardatenDescription =
      this.enhanceConfigDescriptionForKlardatenTask(enhancedDescription);
    const filteredDescription = filterConfigFields(
      klardatenDescription,
      this.config
    );

    return getConfigEntries(filteredDescription);
  }

  private enhanceConfigDescriptionForKlardatenTask(
    configDescription: Record<string, InspectorFieldDescription> | undefined
  ): Record<string, InspectorFieldDescription> | undefined {
    const shape = this.getKlardatenTaskConfigShape();
    if (
      !configDescription ||
      !this.klardatenCredentials?.hasKey ||
      !shape ||
      this.hasExplicitKlardatenNodeAuth(shape)
    ) {
      return configDescription;
    }

    const enhanced = { ...configDescription };
    for (const key of shape.authKeys) {
      delete enhanced[key];
    }

    if (enhanced[shape.instanceKey]) {
      enhanced[shape.instanceKey] = {
        ...enhanced[shape.instanceKey],
        name: 'Client instance',
        required: false,
        defaultValue: '',
        options: [
          { value: '', label: this.getKlardatenDefaultInstanceLabel() },
          ...this.klardatenInstances
            .filter((instance) => typeof instance.id === 'string')
            .map((instance) => ({
              value: instance.id,
              label: getKlardatenInstanceLabel(instance),
            })),
        ],
      };
    }

    if (enhanced[shape.profileKey]) {
      enhanced[shape.profileKey] = {
        ...enhanced[shape.profileKey],
        name: 'Profile',
        required: false,
        defaultValue: '',
        options: [
          this.getKlardatenDefaultProfileOption(),
          ...this.klardatenProfiles.map((profile) => ({
            value: profile.profile_id,
            label: getKlardatenProfilePrimaryLabel(profile),
            description: getKlardatenProfileSecondaryLabel(profile),
          })),
        ],
      };
    }

    return enhanced;
  }

  private isCodeEditorComponent(
    component?: string
  ): component is 'textarea' | 'code' {
    return component === 'textarea' || component === 'code';
  }

  private isFieldFullscreen(key: string): boolean {
    return this.fullscreenConfigKey === key;
  }

  private toggleFullscreenForKey(key: string, e?: Event) {
    e?.stopPropagation();
    this.fullscreenConfigKey = this.isFieldFullscreen(key) ? undefined : key;
  }

  private handleCodeEditorFullscreenChange(
    key: string,
    e: CustomEvent<{ fullscreen: boolean }>
  ) {
    this.fullscreenConfigKey = e.detail.fullscreen ? key : undefined;
  }

  private renderFieldLabelText(key: string, label: string) {
    return html`<span
      class="config-field-label"
      @click=${() => this.focusControlByName(key)}
      >${label}</span
    >`;
  }

  private renderFieldTooltipLabel(
    key: string,
    label: string,
    description: string
  ) {
    return html`<overlay-trigger placement="right-start" triggered-by="hover">
      <span
        class="config-field-label"
        slot="trigger"
        @click=${() => this.focusControlByName(key)}
        >${label}</span
      >
      <sp-tooltip slot="hover-content"> ${description} </sp-tooltip>
    </overlay-trigger>`;
  }

  private renderFullscreenButton(key: string) {
    const isFullscreen = this.isFieldFullscreen(key);
    return html`<sp-action-button
      quiet
      class="label-fullscreen-button"
      label=${isFullscreen ? 'Exit fullscreen editor' : 'Fullscreen editor'}
      @click=${(e: Event) => this.toggleFullscreenForKey(key, e)}
    >
      ${
        isFullscreen
          ? html`<sp-icon-full-screen-exit slot="icon"></sp-icon-full-screen-exit>`
          : html`<sp-icon-full-screen slot="icon"></sp-icon-full-screen>`
      }
    </sp-action-button>`;
  }

  private renderFieldLabel(
    key: string,
    label: string,
    description?: string,
    allowFullscreen = false
  ) {
    const labelContent = description
      ? this.renderFieldTooltipLabel(key, label, description)
      : this.renderFieldLabelText(key, label);
    const fullscreenButton = allowFullscreen
      ? this.renderFullscreenButton(key)
      : null;

    return html`
      ${labelContent}
      ${fullscreenButton}
    `;
  }

  private renderPickerField(
    key: string,
    label: string,
    value: unknown,
    options: InspectorFieldOption[]
  ) {
    const stringValue = value == null ? '' : String(value);
    const pickerLabel = getPickerPlaceholderLabel(label, stringValue, options);
    return html`<sp-picker
      name=${key}
      label=${pickerLabel}
      .value=${live(stringValue)}
      @change=${(e: Event) => this.onConfigCommitChange(key, e)}
    >
      ${options.map((option) => {
        const description = getOptionDescription(option);
        return html`<sp-menu-item value=${getOptionValue(option)}>
            ${getOptionLabel(option)}
            ${
              description
                ? html`<span slot="description">${description}</span>`
                : null
            }
          </sp-menu-item>`;
      })}
    </sp-picker>`;
  }

  private renderCodeEditorField(
    key: string,
    description: InspectorFieldDescription,
    value: unknown,
    mode: 'template' | 'javascript',
    rows: number,
    maxRows: number,
    placeholder: string
  ) {
    return html`<sb-code-editor
      name=${description.name ?? key}
      mode=${mode}
      .value=${String(value ?? '')}
      .placeholder=${placeholder}
      .rows=${rows}
      .maxRows=${maxRows}
      .fullscreen=${this.isFieldFullscreen(key)}
      @input=${(e: Event) => this.onConfigInput(key, e)}
      @editor-commit=${(e: Event) => this.onConfigEditorCommit(key, e)}
      @fullscreen-change=${(e: CustomEvent<{ fullscreen: boolean }>) =>
        this.handleCodeEditorFullscreenChange(key, e)}
    ></sb-code-editor>`;
  }

  private renderConfigComponentField(
    key: string,
    description: InspectorFieldDescription,
    value: unknown,
    multilineFallback: boolean
  ) {
    const placeholder = description.placeholder ?? '';
    if (description.component === 'textarea') {
      const rows = resolveTextareaRows(description, value);
      const maxRows =
        typeof description.maxRows === 'number' && description.maxRows >= rows
          ? description.maxRows
          : 12;
      return this.renderCodeEditorField(
        key,
        description,
        value,
        'template',
        rows,
        maxRows,
        placeholder
      );
    }

    if (description.component === 'code') {
      const rows = resolveEditorRows(description, value, 4, 14);
      const maxRows =
        typeof description.maxRows === 'number' && description.maxRows >= rows
          ? description.maxRows
          : 14;
      return this.renderCodeEditorField(
        key,
        description,
        value,
        'javascript',
        rows,
        maxRows,
        placeholder
      );
    }

    return html`<sp-textfield
      name=${key}
      .value=${live(String(value))}
      .placeholder=${placeholder}
      ?multiline=${multilineFallback}
      ?grows=${multilineFallback}
      @input=${(e: Event) => this.onConfigInput(key, e)}
      @change=${(e: Event) => this.onConfigCommitChange(key, e)}
    ></sp-textfield>`;
  }

  private isSecretRevealed(key: string): boolean {
    return this.revealedSecretKeys.has(key);
  }

  private toggleSecretReveal(key: string, e: Event): void {
    e.stopPropagation();
    if (this.revealedSecretKeys.has(key)) {
      this.revealedSecretKeys.delete(key);
    } else {
      this.revealedSecretKeys.add(key);
    }
    this.requestUpdate();
  }

  private renderSecretFieldControl(key: string, value: unknown) {
    const revealed = this.isSecretRevealed(key);
    const stringValue = String(value ?? '');
    const hasSecretValue = stringValue.length > 0;
    return html`
      <div class="secret-field-control">
        <sp-textfield
          class="secret-field-input"
          name=${key}
          type=${revealed ? 'text' : 'password'}
          .value=${live(stringValue)}
          @input=${(e: Event) => this.onConfigInput(key, e)}
          @change=${(e: Event) => this.onConfigCommitChange(key, e)}
        ></sp-textfield>
        ${
          hasSecretValue
            ? html`<sp-action-button
                quiet
                class="secret-toggle-button"
                label=${revealed ? 'Hide secret value' : 'Show secret value'}
                @click=${(e: Event) => this.toggleSecretReveal(key, e)}
              >
                ${
                  revealed
                    ? html`<sp-icon-visibility-off
                        slot="icon"
                      ></sp-icon-visibility-off>`
                    : html`<sp-icon-visibility
                        slot="icon"
                      ></sp-icon-visibility>`
                }
              </sp-action-button>`
            : null
        }
      </div>
    `;
  }

  private canInsertRuntimeReference(
    description: InspectorFieldDescription
  ): boolean {
    if (description.options?.length) {
      return false;
    }
    const types = description.types ?? [];
    if (
      types.includes('boolean') ||
      types.includes('int') ||
      types.includes('float') ||
      types.includes('object') ||
      types.some((type) => type.startsWith('array<'))
    ) {
      return false;
    }
    return types.length === 0 || types.includes('string');
  }

  private renderRuntimeReferenceMenu(
    key: string,
    description: InspectorFieldDescription
  ) {
    if (
      !this.canInsertRuntimeReference(description) ||
      this.runtimeValues.length === 0
    ) {
      return null;
    }

    const runtimeValues = [...this.runtimeValues].sort((a, b) =>
      `${a.kind}:${a.key}`.localeCompare(`${b.kind}:${b.key}`)
    );

    return html`
      <sp-action-menu
        quiet
        class="runtime-reference-menu"
        label=${msg('Insert runtime value')}
      >
        ${runtimeValues.map(
          (value) => html`
            <sp-menu-item
              @click=${(e: Event) => this.insertRuntimeReference(key, value, e)}
            >
              ${value.kind === 'secret' ? msg('Secret') : msg('Config')}:
              ${value.key}
            </sp-menu-item>
          `
        )}
      </sp-action-menu>
    `;
  }

  private renderControlWithRuntimeReference(
    key: string,
    description: InspectorFieldDescription,
    control: unknown
  ) {
    const menu = this.renderRuntimeReferenceMenu(key, description);
    if (!menu) {
      return control;
    }

    return html`
      <div class="field-control-with-reference">
        ${control}
        ${menu}
      </div>
    `;
  }

  private insertRuntimeReference(
    key: string,
    runtimeValue: RuntimeValue,
    event: Event
  ) {
    event.stopPropagation();
    const token = `{{ ${runtimeValue.kind}:${runtimeValue.key} }}`;

    this.config = {
      ...(this.config ?? {}),
      [key]: token,
    };
    this.pendingConfigKeys.delete(key);
    this.pendingConfigValues.delete(key);

    const workflowId = this.workflowId;
    const taskId = this.taskId;
    if (!workflowId || taskId == null) {
      return;
    }

    const types = this.definition?.configDescription?.[key]?.types;
    void this.persistConfigChange(workflowId, taskId, key, token, types);
    void this.updateComplete.then(() => {
      this.focusControlByName(key);
    });
  }

  private renderFieldControl(
    key: string,
    description: InspectorFieldDescription,
    value: unknown,
    multilineFallback: boolean
  ) {
    if (isSecretField(description)) {
      return this.renderControlWithRuntimeReference(
        key,
        description,
        this.renderSecretFieldControl(key, value)
      );
    }

    if (description.types?.includes('boolean')) {
      return html`<sp-switch
        name=${key}
        .checked=${live(Boolean(value))}
        @change=${(e: Event) => this.onConfigCommitChange(key, e)}
      ></sp-switch>`;
    }

    if (description.options?.length) {
      return this.renderPickerField(
        key,
        description.name ?? key,
        value,
        description.options as InspectorFieldOption[]
      );
    }

    return this.renderControlWithRuntimeReference(
      key,
      description,
      this.renderConfigComponentField(
        key,
        description,
        value,
        multilineFallback
      )
    );
  }

  private renderConfigField(
    key: string,
    description: InspectorFieldDescription
  ) {
    const rawValue = resolveConfigRawValue(this.config, key, description);
    const value = normalizeConfigDisplayValue(rawValue, description.types);
    const multilineFallback = shouldUseMultiline(description, value);
    const isStacked = isStackedField(description);
    const supportsFullscreen = this.isCodeEditorComponent(
      description.component
    );
    const label = description.name ?? key;
    const tooltip = description.description;

    return html`
      <div
        class="config-field ${isStacked ? 'config-field--stacked' : ''}"
        @click=${() => this.focusControlByName(key)}
      >
        <div class="config-field-label-wrap">
          ${this.renderFieldLabel(key, label, tooltip, supportsFullscreen)}
        </div>
        <div class="config-field-control">
          ${this.renderFieldControl(key, description, value, multilineFallback)}
        </div>
      </div>
    `;
  }

  private renderTaskLabelField() {
    return html`
      <section data-testid="inspector-label">
        <div
          class="config-field"
          @click=${() => this.focusControlByName('task-label')}
        >
          <div class="config-field-label-wrap">
            <span
              class="config-field-label"
              @click=${() => this.focusControlByName('task-label')}
              >${msg('Label')}</span
            >
          </div>
          <div class="config-field-control">
            <sp-textfield
              name="task-label"
              .value=${live(this.label ?? '')}
              @input=${(e: Event) => this.onLabelInput(e)}
              @change=${(e: Event) => this.onLabelChange(e)}
            ></sp-textfield>
          </div>
        </div>
      </section>
    `;
  }

  private getApprovalGuards(): TaskApprovalGuards {
    return {
      preExecution: this.approvalGuards?.preExecution === true,
      postExecution: this.approvalGuards?.postExecution === true,
    };
  }

  private approvalGuardsEqual(
    left: TaskApprovalGuards,
    right: TaskApprovalGuards
  ): boolean {
    return (
      left.preExecution === right.preExecution &&
      left.postExecution === right.postExecution
    );
  }

  private dispatchApprovalGuardsChanged(approvalGuards: TaskApprovalGuards) {
    this.dispatchEvent(
      new CustomEvent('task-approval-guards-changed', {
        detail: { value: { ...approvalGuards } },
        bubbles: true,
        composed: true,
      })
    );
  }

  private async persistApprovalGuards(
    approvalGuards: TaskApprovalGuards,
    previousApprovalGuards: TaskApprovalGuards
  ) {
    const workflowId = this.workflowId;
    const taskId = this.taskId;
    if (!workflowId || taskId == null) return;

    try {
      await api.workflows.updateTask(workflowId, taskId, {
        approvalGuards,
      });
      if (this.workflowId === workflowId && this.taskId === taskId) {
        const currentApprovalGuards = this.getApprovalGuards();
        if (this.approvalGuardsEqual(currentApprovalGuards, approvalGuards)) {
          this.approvalGuardError = undefined;
          this.dispatchApprovalGuardsChanged(approvalGuards);
        }
      }
    } catch (err) {
      if (this.workflowId !== workflowId || this.taskId !== taskId) {
        return;
      }

      const currentApprovalGuards = this.getApprovalGuards();
      if (this.approvalGuardsEqual(currentApprovalGuards, approvalGuards)) {
        this.approvalGuards = { ...previousApprovalGuards };
        this.dispatchApprovalGuardsChanged(previousApprovalGuards);
      }
      this.approvalGuardError =
        err instanceof Error && err.message
          ? err.message
          : msg('Failed to update approval guards.');
    }
  }

  private onApprovalGuardChange(key: keyof TaskApprovalGuards, e: Event) {
    const checked = Boolean(
      (e.target as { checked?: boolean } | null)?.checked
    );
    const previousApprovalGuards = this.getApprovalGuards();
    const approvalGuards = {
      ...previousApprovalGuards,
      [key]: checked,
    };
    this.approvalGuards = approvalGuards;
    this.approvalGuardError = undefined;
    void this.persistApprovalGuards(approvalGuards, previousApprovalGuards);
  }

  private renderApprovalGuardsField(
    key: keyof TaskApprovalGuards,
    label: string
  ) {
    const value = this.getApprovalGuards()[key];
    return html`
      <div
        class="config-field"
        @click=${() => this.focusControlByName(`approval-${key}`)}
      >
        <div class="config-field-label-wrap">
          <span
            class="config-field-label"
            @click=${() => this.focusControlByName(`approval-${key}`)}
            >${label}</span
          >
        </div>
        <div class="config-field-control">
          <sp-switch
            name=${`approval-${key}`}
            .checked=${live(value)}
            @change=${(e: Event) => this.onApprovalGuardChange(key, e)}
          ></sp-switch>
        </div>
      </div>
    `;
  }

  private renderApprovalGuardsSection() {
    return html`
      <section
        class="approval-guards-section"
        data-testid="inspector-approvals"
      >
        ${this.renderApprovalGuardsField('preExecution', msg('Approve before'))}
        ${this.renderApprovalGuardsField('postExecution', msg('Approve after'))}
        ${
          this.approvalGuardError
            ? html`<div class="config-field-error">
                ${this.approvalGuardError}
              </div>`
            : html``
        }
      </section>
    `;
  }

  private renderKlardatenSetupHint() {
    if (!this.shouldShowKlardatenSetupHint()) return null;

    return html`
      <div
        class="klardaten-setup-hint"
        data-testid="klardaten-setup-hint"
        role="note"
      >
        <div class="klardaten-setup-hint-copy">
          <span class="klardaten-setup-hint-title">Klardaten defaults</span>
          <p class="klardaten-setup-hint-text">
            Save an API key to show instance and profile defaults here.
          </p>
        </div>
        <button
          class="klardaten-setup-hint-button"
          type="button"
          aria-label="Open Klardaten settings"
          @click=${(e: Event) => this.openKlardatenSettingsFromHint(e)}
        >
          Set up key
        </button>
      </div>
    `;
  }

  private renderTaskConfigFields(entries: InspectorFieldEntries) {
    return html`
      <section data-testid="inspector-config">
        ${this.renderKlardatenSetupHint()}
        ${
          entries.length
            ? repeat(
                entries,
                ([key]) => key,
                ([key, description]) => this.renderConfigField(key, description)
              )
            : html`<div>${msg('No configuration')}</div>`
        }
      </section>
    `;
  }

  render() {
    if (!this.taskId) return html``;
    const configEntries = this.getConfigEntries();

    return html`
      ${this.renderTaskLabelField()} ${this.renderApprovalGuardsSection()}
      ${this.renderTaskConfigFields(configEntries)}
    `;
  }
}

@localized()
export class SbTaskGroupInspector extends BaseInspector {
  static properties = {
    workflowId: {},
    taskGroupId: {},
    taskGroup: {},
    groupTitle: { state: true },
  } as const;

  declare workflowId?: Id;
  declare taskGroupId?: Id;
  declare taskGroup?: { title?: string };
  declare groupTitle: string;

  constructor() {
    super();
    this.workflowId = undefined;
    this.taskGroupId = undefined;
    this.taskGroup = undefined;
    this.groupTitle = '';
  }

  render() {
    if (!this.taskGroupId) return html``;
    const title = this.groupTitle || this.taskGroup?.title || '';
    return html`
      <section data-testid="inspector-task-group">
        <h3>${msg('Task group')}</h3>
        <label class="config-field">
          <span>${msg('Title')}</span>
          <sp-textfield
            name="task-group-title"
            .value=${live(title)}
            placeholder=${msg('Group title')}
            @input=${(e: Event) => this.onGroupTitleInput(e)}
          ></sp-textfield>
        </label>
      </section>
    `;
  }

  private onGroupTitleInput(e: Event) {
    const val =
      (
        e.target as
          | (EventTarget & {
              value?: string;
            })
          | null
      )?.value ?? '';
    this.groupTitle = val;
    this.onGroupTitleChange(val);
  }

  private onGroupTitleChange = debounce(async (value: string) => {
    if (!this.workflowId || this.taskGroupId == null) return;
    const title = (value ?? '').trim();
    try {
      await api.workflows.updateTaskGroup(this.workflowId, this.taskGroupId, {
        title,
      });
      this.groupTitle = title;
      this.dispatchEvent(
        new CustomEvent('task-group-config-changed', {
          detail: { key: 'title', value: title },
          bubbles: true,
          composed: true,
        })
      );
    } catch (err) {
      console.error('Failed to update task group', err);
    }
  }, 300);
}

@localized()
export class SbConnectionInspector extends BaseInspector {
  static properties = {
    workflowId: {},
    connectionId: {},
    connectionName: {},
    nameErrors: { state: true },
  } as const;

  declare workflowId?: Id;
  declare connectionId?: Id;
  declare connectionName: string;
  declare nameErrors: Record<string, string>;

  constructor() {
    super();
    this.workflowId = undefined;
    this.connectionId = undefined;
    this.connectionName = '';
    this.nameErrors = {};
  }

  get nameError(): string | undefined {
    const key = this.connectionScopeKey(this.workflowId, this.connectionId);
    return key ? this.nameErrors[key] : undefined;
  }

  private connectionScopeKey(
    workflowId?: Id,
    connectionId?: Id
  ): string | undefined {
    if (!workflowId || connectionId == null) {
      return undefined;
    }
    return `${workflowId}:${connectionId}`;
  }

  private setNameErrorForTarget(
    workflowId?: Id,
    connectionId?: Id,
    error?: string
  ) {
    const key = this.connectionScopeKey(workflowId, connectionId);
    if (!key) {
      return;
    }

    if (!error) {
      if (this.nameErrors[key] === undefined) {
        return;
      }
      const { [key]: _removed, ...rest } = this.nameErrors;
      this.nameErrors = rest;
      return;
    }

    if (this.nameErrors[key] === error) {
      return;
    }
    this.nameErrors = { ...this.nameErrors, [key]: error };
  }

  private validateConnectionName(value: string): string | undefined {
    if (!value || value.trim().length === 0) {
      return msg('Name is required.');
    }
    if (!CONNECTION_NAME_PATTERN.test(value)) {
      return msg(
        'Use a JavaScript key format (starts with a letter; then letters, numbers, _ or $).'
      );
    }
    return undefined;
  }

  private onNameInput(e: Event) {
    const value =
      (
        e.target as
          | (EventTarget & {
              value?: string;
            })
          | null
      )?.value ?? '';
    this.connectionName = value;
    this.setNameErrorForTarget(
      this.workflowId,
      this.connectionId,
      this.validateConnectionName(value)
    );
  }

  private onNameBlur(e: Event) {
    const value =
      (
        e.target as
          | (EventTarget & {
              value?: string;
            })
          | null
      )?.value ?? this.connectionName;
    this.onNameChange(value, this.workflowId, this.connectionId);
  }

  private onNameChange = debounce(
    async (value: string, workflowId?: Id, connectionId?: Id) => {
      if (!workflowId || connectionId == null) return;

      const validationError = this.validateConnectionName(value);
      if (validationError) {
        this.setNameErrorForTarget(workflowId, connectionId, validationError);
        return;
      }

      try {
        await api.workflows.updateConnection(workflowId, connectionId, {
          name: value,
        });
        this.setNameErrorForTarget(workflowId, connectionId, undefined);
        this.dispatchEvent(
          new CustomEvent('connection-name-changed', {
            detail: { id: connectionId, value },
            bubbles: true,
            composed: true,
          })
        );
      } catch (err) {
        this.setNameErrorForTarget(
          workflowId,
          connectionId,
          err instanceof Error && err.message
            ? err.message
            : msg('Failed to update connection name.')
        );
      }
    },
    300
  );

  render() {
    if (!this.connectionId) return html``;
    const nameError = this.nameError;
    return html`
      <section data-testid="inspector-label">
        <div class="config-field">
          <div class="config-field-label-wrap">
            <span class="config-field-label">${msg('Name')}</span>
          </div>
          <div class="config-field-control">
            <sp-textfield
              class="connection-name-field"
              name="connection-name"
              pattern="[A-Za-z][A-Za-z0-9_$]*"
              title=${msg('Use a JavaScript key format (starts with a letter; then letters, numbers, _ or $).')}
              ?invalid=${Boolean(nameError)}
              .value=${live(this.connectionName ?? '')}
              @input=${(e: Event) => this.onNameInput(e)}
              @focusout=${(e: Event) => this.onNameBlur(e)}
            ></sp-textfield>
          </div>
        </div>
        ${
          nameError
            ? html`<div class="config-field-error">${nameError}</div>`
            : html``
        }
      </section>
    `;
  }
}

customElements.define('sb-task-inspector', SbTaskInspector);
customElements.define('sb-task-group-inspector', SbTaskGroupInspector);
customElements.define('sb-connection-inspector', SbConnectionInspector);
